import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import siteAnalyticsService, {
  Campaign,
  CampaignInput,
  CampaignInsights,
} from '../../services/siteAnalytics.service';
import { useDateRange, RangeSelector, fa, faDecimal, fmtDuration, decodePath } from './useDateRange';
import { PALETTE, defaultLineOptions } from './chartConfig';
import './analytics.css';

const MEDIUM_OPTIONS = ['social', 'paid', 'email', 'referral', 'organic', 'sms', 'influencer', 'other'];

/**
 * پریست کانال‌ها — یک کلیک، utm_source/utm_medium استاندارد را پر می‌کند تا
 * گزارش Acquisition (تفکیک بله/روبیکا/ایتا/…) دقیق بماند. مقادیر utm_source
 * با رجیستری بک‌اند (trafficSources.ts) هماهنگ‌اند.
 */
const CHANNEL_PRESETS: Array<{ key: string; label: string; utmSource: string; utmMedium: string }> = [
  { key: 'bale', label: 'بله', utmSource: 'bale', utmMedium: 'social' },
  { key: 'rubika', label: 'روبیکا', utmSource: 'rubika', utmMedium: 'social' },
  { key: 'eitaa', label: 'ایتا', utmSource: 'eitaa', utmMedium: 'social' },
  { key: 'soroush', label: 'سروش', utmSource: 'soroush', utmMedium: 'social' },
  { key: 'telegram', label: 'تلگرام', utmSource: 'telegram', utmMedium: 'social' },
  { key: 'whatsapp', label: 'واتساپ', utmSource: 'whatsapp', utmMedium: 'social' },
  { key: 'instagram', label: 'اینستاگرام', utmSource: 'instagram', utmMedium: 'social' },
  { key: 'youtube', label: 'یوتیوب', utmSource: 'youtube', utmMedium: 'social' },
  { key: 'google', label: 'گوگل (تبلیغ)', utmSource: 'google', utmMedium: 'paid' },
  { key: 'sms', label: 'پیامک', utmSource: 'sms', utmMedium: 'sms' },
  { key: 'email', label: 'ایمیل', utmSource: 'email', utmMedium: 'email' },
];

/** برچسب فارسی منبع برای chip کانال در لیست */
const SOURCE_FA: Record<string, string> = {
  bale: 'بله', rubika: 'روبیکا', eitaa: 'ایتا', soroush: 'سروش', telegram: 'تلگرام',
  whatsapp: 'واتساپ', instagram: 'اینستاگرام', youtube: 'یوتیوب', google: 'گوگل',
  sms: 'پیامک', email: 'ایمیل', twitter: 'ایکس', facebook: 'فیسبوک', aparat: 'آپارات',
};

const PAGE_SIZE = 15;

const emptyForm: CampaignInput = {
  name: '',
  code: '',
  utmSource: '',
  utmMedium: 'social',
  destinationPath: '/',
  utmTerm: '',
  utmContent: '',
  description: '',
  isActive: true,
};

const SiteAnalyticsCampaigns: React.FC = () => {
  const dr = useDateRange('30d');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // فیلترهای لیست
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [page, setPage] = useState(1);

  // selected campaign detail
  const [selected, setSelected] = useState<Campaign | null>(null);
  const [insights, setInsights] = useState<CampaignInsights | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  // create/edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [form, setForm] = useState<CampaignInput>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);

  const [copied, setCopied] = useState<string | null>(null);
  const listReqIdRef = useRef(0);
  const insightsReqIdRef = useRef(0);

  const loadList = async () => {
    const reqId = ++listReqIdRef.current;
    setLoading(true);
    setErr(null);
    try {
      const data = await siteAnalyticsService.listCampaigns(dr.range);
      if (reqId !== listReqIdRef.current) return;
      setCampaigns(data || []);
    } catch (e: any) {
      if (reqId !== listReqIdRef.current) return;
      setErr(e?.message || 'خطا در دریافت کمپین‌ها');
    } finally {
      if (reqId === listReqIdRef.current) setLoading(false);
    }
  };

  const loadInsights = async (c: Campaign) => {
    const reqId = ++insightsReqIdRef.current;
    setSelected(c);
    setInsights(null);
    setInsightsLoading(true);
    try {
      const data = await siteAnalyticsService.getCampaignInsights(c.code, dr.range);
      if (reqId !== insightsReqIdRef.current) return; // پاسخ کهنه (کمپین/بازهٔ دیگر) — نادیده
      setInsights(data);
    } catch (e: any) {
      if (reqId !== insightsReqIdRef.current) return;
      setErr(e?.message || 'خطا در دریافت آمار کمپین');
    } finally {
      if (reqId === insightsReqIdRef.current) setInsightsLoading(false);
    }
  };

  useEffect(() => {
    if (selected) loadInsights(selected);
    else loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dr.range.from, dr.range.to, dr.refreshKey]);

  // فیلتر + صفحه‌بندی سمت کلاینت
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return campaigns.filter((c) => {
      if (statusFilter === 'active' && !c.isActive) return false;
      if (statusFilter === 'inactive' && c.isActive) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        (c.utmSource || '').toLowerCase().includes(q) ||
        (c.description || '').toLowerCase().includes(q)
      );
    });
  }, [campaigns, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const pageRows = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);
  useEffect(() => { setPage(1); }, [search, statusFilter]);

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(text);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      /* ignore */
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormErr(null);
    setDialogOpen(true);
  };

  const applyPreset = (p: (typeof CHANNEL_PRESETS)[number]) => {
    setForm((f) => ({
      ...f,
      utmSource: p.utmSource,
      utmMedium: p.utmMedium,
      name: f.name || `کمپین ${p.label}`,
      code: f.code || `${p.key}-${new Date().toISOString().slice(2, 10).replace(/-/g, '')}`,
    }));
  };

  const openEdit = (c: Campaign) => {
    setEditing(c);
    setForm({
      name: c.name,
      code: c.code,
      utmSource: c.utmSource || '',
      utmMedium: c.utmMedium || '',
      utmTerm: c.utmTerm || '',
      utmContent: c.utmContent || '',
      destinationPath: c.destinationPath || '/',
      description: c.description || '',
      isActive: c.isActive,
    });
    setFormErr(null);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name?.trim()) {
      setFormErr('نام کمپین الزامی است');
      return;
    }
    setSaving(true);
    setFormErr(null);
    try {
      if (editing) {
        await siteAnalyticsService.updateCampaign(editing._id, form);
      } else {
        await siteAnalyticsService.createCampaign(form);
      }
      setDialogOpen(false);
      await loadList();
    } catch (e: any) {
      setFormErr(e?.message || 'خطا در ذخیره کمپین');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c: Campaign) => {
    if (!window.confirm(`حذف کمپین «${c.name}»؟ داده‌های آماری ثبت‌شده باقی می‌مانند.`)) return;
    try {
      await siteAnalyticsService.deleteCampaign(c._id);
      await loadList();
    } catch (e: any) {
      setErr(e?.message || 'خطا در حذف کمپین');
    }
  };

  const toggleActive = async (c: Campaign) => {
    try {
      await siteAnalyticsService.updateCampaign(c._id, { isActive: !c.isActive });
      await loadList();
    } catch (e: any) {
      setErr(e?.message || 'خطا در تغییر وضعیت کمپین');
    }
  };

  /** «نرخ ورود» = سشن‌های بازه ÷ کلیک انسانی بازه (چند کلیک بی‌فرود = مشکل لینک/سرعت) */
  const entryRate = (sessions: number | undefined, clicks: number | null | undefined): string => {
    if (!clicks || clicks <= 0 || sessions === undefined) return '—';
    return `${faDecimal(Math.min(999, (sessions / clicks) * 100), 0)}٪`;
  };

  const channelChip = (c: Campaign) => {
    const src = (c.utmSource || '').toLowerCase();
    const label = SOURCE_FA[src] || c.utmSource || '—';
    return (
      <span
        style={{
          fontSize: 11,
          padding: '2px 8px',
          borderRadius: 10,
          background: 'var(--color-bg-tertiary, rgba(127,127,127,.12))',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    );
  };

  // ============================ DETAIL VIEW ============================
  if (selected) {
    const t = insights?.totals;
    const clickMap = new Map<string, number>((insights?.clicksSeries || []).map((p) => [p.date, p.clicks]));
    const lineData = {
      labels: (insights?.timeSeries || []).map((p) =>
        new Date(p.date + 'T12:00:00').toLocaleDateString('fa-IR', { month: 'short', day: 'numeric' })
      ),
      datasets: [
        {
          label: 'کلیک لینک',
          data: (insights?.timeSeries || []).map((p) => clickMap.get(p.date) || 0),
          borderColor: PALETTE.purple,
          backgroundColor: 'transparent',
          borderDash: [6, 4],
          tension: 0.35,
        },
        {
          label: 'سشن‌ها',
          data: (insights?.timeSeries || []).map((p) => p.sessions),
          borderColor: PALETTE.primary,
          backgroundColor: PALETTE.primaryLight,
          tension: 0.35,
          fill: true,
        },
        {
          label: 'بازدیدکننده',
          data: (insights?.timeSeries || []).map((p) => p.visitors),
          borderColor: PALETTE.success,
          backgroundColor: PALETTE.successLight,
          tension: 0.35,
        },
        {
          label: 'بازدید صفحه',
          data: (insights?.timeSeries || []).map((p) => p.pageViews),
          borderColor: PALETTE.warning,
          backgroundColor: PALETTE.warningLight,
          tension: 0.35,
        },
      ],
    };

    const maxFunnel = insights?.funnel?.[0]?.visitors || 0;

    const kvTable = (
      title: string,
      rows: Array<{ value: string; count: number }> | undefined,
      color: string,
      valueLabel?: (v: string) => string
    ) => {
      const max = rows && rows.length ? Math.max(...rows.map((r) => r.count)) : 0;
      return (
        <div className="analytics-section">
          <div className="analytics-section-header">
            <h3>{title}</h3>
          </div>
          {!rows || rows.length === 0 ? (
            <div className="analytics-empty">داده‌ای نیست</div>
          ) : (
            <table className="analytics-table">
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>{valueLabel ? valueLabel(r.value) : r.value || '(نامشخص)'}</td>
                    <td style={{ width: 70 }}>{fa(r.count)}</td>
                    <td style={{ width: 130 }}>
                      <span className="pct-bar" style={{ width: `${max ? (r.count / max) * 120 : 0}px`, background: color }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    };

    const MEDIUM_FA: Record<string, string> = {
      direct: 'مستقیم', organic: 'جستجوی ارگانیک', referral: 'ارجاعی', social: 'شبکه اجتماعی',
      paid: 'تبلیغ پولی', email: 'ایمیل', other: 'سایر',
    };

    return (
      <div className="analytics-page">
        <div className="analytics-header">
          <h2>
            <button className="link-btn" onClick={() => { setSelected(null); setInsights(null); loadList(); }}>
              ← کمپین‌ها
            </button>
            {' / '}{selected.name}
          </h2>
          <div className="analytics-controls">
            <RangeSelector {...dr} />
            <button onClick={dr.refresh} disabled={insightsLoading}>
              {insightsLoading ? 'در حال بارگذاری…' : 'بروزرسانی'}
            </button>
          </div>
        </div>

        {/* link strip */}
        <div className="analytics-section" style={{ marginBottom: 16 }}>
          <div className="campaign-link-row">
            <span className="campaign-link-label">لینک کوتاه:</span>
            <code className="campaign-link">{selected.shortLink}</code>
            <button className="copy-btn" onClick={() => copy(selected.shortLink)}>
              {copied === selected.shortLink ? '✓ کپی شد' : 'کپی'}
            </button>
          </div>
          <div className="campaign-link-row">
            <span className="campaign-link-label">لینک کامل UTM:</span>
            <code className="campaign-link" style={{ direction: 'ltr' }}>{decodePath(selected.targetUrl)}</code>
            <button className="copy-btn" onClick={() => copy(selected.targetUrl)}>
              {copied === selected.targetUrl ? '✓ کپی شد' : 'کپی'}
            </button>
          </div>
        </div>

        {insightsLoading && <div className="analytics-empty">در حال بارگذاری…</div>}

        {t && (
          <>
            <div className="analytics-kpi-grid">
              <div className="analytics-kpi-card purple">
                <div className="accent" />
                <div className="label" title="کلیک انسانی لینک کوتاه در بازهٔ انتخابی (کلیک بات‌ها و پیش‌نمایش پیام‌رسان‌ها جدا شمرده می‌شود)">
                  کلیک لینک (بازه)
                </div>
                <div className="value">
                  {insights?.clicksInRange === null || insights?.clicksInRange === undefined
                    ? '—'
                    : fa(insights.clicksInRange)}
                </div>
              </div>
              <div className="analytics-kpi-card">
                <div className="accent" />
                <div className="label" title={`کل کلیک انسانی از ابتدای کمپین${insights?.botClicks ? ` · ${fa(insights.botClicks)} کلیک بات جدا شمرده شد` : ''}`}>
                  کلیک لینک (کل)
                </div>
                <div className="value">{fa(insights?.clicks)}</div>
              </div>
              <div className="analytics-kpi-card teal">
                <div className="accent" />
                <div className="label" title="سشن‌های بازه ÷ کلیک انسانی بازه — اگر خیلی کمتر از ۱۰۰٪ است یعنی کلیک‌ها به بازدید تبدیل نمی‌شوند (کندی سایت/لینک خراب)">
                  نرخ ورود
                </div>
                <div className="value">{entryRate(t.sessions, insights?.clicksInRange)}</div>
              </div>
              <div className="analytics-kpi-card green">
                <div className="accent" />
                <div className="label">سشن‌ها</div>
                <div className="value">{fa(t.sessions)}</div>
              </div>
              <div className="analytics-kpi-card">
                <div className="accent" />
                <div className="label">بازدیدکننده</div>
                <div className="value">{fa(t.visitors)}</div>
              </div>
              <div className="analytics-kpi-card purple">
                <div className="accent" />
                <div className="label">کاربر جدید (جذب کمپین)</div>
                <div className="value">{fa(t.newVisitors)}</div>
              </div>
              <div className="analytics-kpi-card teal">
                <div className="accent" />
                <div className="label">کاربر برگشتی</div>
                <div className="value">{fa(t.returningVisitors)}</div>
              </div>
              <div className="analytics-kpi-card orange">
                <div className="accent" />
                <div className="label">بازدید صفحه</div>
                <div className="value">{fa(t.pageViews)}</div>
              </div>
              <div className="analytics-kpi-card green">
                <div className="accent" />
                <div className="label">خرید</div>
                <div className="value">{fa(t.purchases)}</div>
              </div>
              <div className="analytics-kpi-card">
                <div className="accent" />
                <div className="label">درآمد (تومان)</div>
                <div className="value">{fa(t.revenue)}</div>
              </div>
              <div className="analytics-kpi-card green">
                <div className="accent" />
                <div className="label">تبدیل‌های هدف</div>
                <div className="value">{fa(t.conversions)}</div>
                <span className="change flat">خرید، ثبت‌نام، افزودن به سبد…</span>
              </div>
              <div className="analytics-kpi-card purple">
                <div className="accent" />
                <div className="label">نرخ تبدیل</div>
                <div className="value">{faDecimal(t.conversionRatePct)}٪</div>
              </div>
              <div className="analytics-kpi-card red">
                <div className="accent" />
                <div className="label">نرخ پرش</div>
                <div className="value">{fa(t.bounceRate)}٪</div>
              </div>
              <div className="analytics-kpi-card teal">
                <div className="accent" />
                <div className="label">میانگین مدت سشن</div>
                <div className="value">{fmtDuration(t.avgDurationSec)}</div>
              </div>
            </div>

            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>روند کمپین — کلیک لینک در برابر سشن/بازدید</h3>
              </div>
              <div className="analytics-chart-container large">
                {insights && insights.timeSeries.length > 0 ? (
                  <Line data={lineData} options={defaultLineOptions} />
                ) : (
                  <div className="analytics-empty">داده‌ای برای نمایش نیست</div>
                )}
              </div>
            </div>

            {/* Funnel */}
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>قیف تبدیل (ورود → بازدید محصول → سبد → خرید)</h3>
              </div>
              {insights && insights.funnel.length > 0 ? (
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>مرحله</th>
                      <th style={{ width: 90 }}>کاربر</th>
                      <th style={{ width: 90 }}>افت</th>
                      <th>نسبت</th>
                    </tr>
                  </thead>
                  <tbody>
                    {insights.funnel.map((s, i) => (
                      <tr key={i}>
                        <td>{s.step}</td>
                        <td><strong>{fa(s.visitors)}</strong></td>
                        <td>{i === 0 ? '—' : <span className="change down">▼ {fa(s.dropOffPct)}٪</span>}</td>
                        <td>
                          <span
                            className="pct-bar"
                            style={{ width: `${maxFunnel ? (s.visitors / maxFunnel) * 200 : 0}px`, background: PALETTE.primary }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="analytics-empty">داده‌ای نیست</div>
              )}
            </div>

            <div className="analytics-grid-2col">
              {/* Top pages */}
              <div className="analytics-section">
                <div className="analytics-section-header">
                  <h3>پربازدیدترین صفحات</h3>
                </div>
                {insights && insights.topPages.length > 0 ? (
                  <table className="analytics-table">
                    <thead>
                      <tr><th>مسیر</th><th style={{ width: 70 }}>بازدید</th><th style={{ width: 70 }}>کاربر</th></tr>
                    </thead>
                    <tbody>
                      {insights.topPages.map((p, i) => (
                        <tr key={i}>
                          <td style={{ direction: 'ltr', textAlign: 'right', fontSize: 12 }}>{decodePath(p.path)}</td>
                          <td>{fa(p.pageViews)}</td>
                          <td>{fa(p.visitors)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="analytics-empty">داده‌ای نیست</div>
                )}
              </div>

              {/* Actions / events */}
              <div className="analytics-section">
                <div className="analytics-section-header">
                  <h3>اقدامات کاربران (رویدادها)</h3>
                </div>
                {insights && insights.actions.length > 0 ? (
                  <table className="analytics-table">
                    <thead>
                      <tr><th>رویداد</th><th style={{ width: 70 }}>تعداد</th><th style={{ width: 70 }}>کاربر</th></tr>
                    </thead>
                    <tbody>
                      {insights.actions.map((a, i) => (
                        <tr key={i}>
                          <td style={{ direction: 'ltr', textAlign: 'right', fontSize: 12 }}>{a.name}</td>
                          <td>{fa(a.count)}</td>
                          <td>{fa(a.visitors)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="analytics-empty">رویدادی ثبت نشده</div>
                )}
              </div>
            </div>

            <div className="analytics-grid-2col">
              {kvTable('بر اساس دستگاه', insights?.byDevice, PALETTE.purple)}
              {kvTable('بر اساس کشور', insights?.byCountry, PALETTE.success)}
            </div>
            <div className="analytics-grid-2col">
              {kvTable('بر اساس utm_source', insights?.bySource, PALETTE.warning, (v) => SOURCE_FA[(v || '').toLowerCase()] || v || '(نامشخص)')}
              {kvTable('صفحات ورود (Landing)', insights?.landingPages, PALETTE.teal)}
            </div>
            <div className="analytics-grid-2col">
              {kvTable('بر اساس medium', insights?.byMedium, PALETTE.primary, (v) => MEDIUM_FA[v] || v || '(نامشخص)')}
              {/* وضعیت ورود کاربران کمپین */}
              <div className="analytics-section">
                <div className="analytics-section-header">
                  <h3>وضعیت ورود کاربران</h3>
                </div>
                {insights?.authStats ? (
                  <table className="analytics-table">
                    <tbody>
                      <tr>
                        <td>سشن با کاربر لاگین‌کرده</td>
                        <td style={{ width: 90 }}>{fa(insights.authStats.authenticated)}</td>
                      </tr>
                      <tr>
                        <td>سشن مهمان</td>
                        <td>{fa(insights.authStats.anonymous)}</td>
                      </tr>
                      <tr>
                        <td>نسبت لاگین</td>
                        <td>
                          {insights.authStats.authenticated + insights.authStats.anonymous > 0
                            ? `${fa(Math.round((insights.authStats.authenticated / (insights.authStats.authenticated + insights.authStats.anonymous)) * 100))}٪`
                            : '—'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <div className="analytics-empty">داده‌ای نیست</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // ============================ LIST VIEW ============================
  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h2>کمپین‌ها و لینک‌های اختصاصی</h2>
        <div className="analytics-controls">
          <RangeSelector {...dr} />
          <button onClick={dr.refresh} disabled={loading}>
            {loading ? 'در حال بارگذاری…' : 'بروزرسانی'}
          </button>
          <button onClick={openCreate} style={{ background: PALETTE.primary, color: '#fff' }}>
            + کمپین جدید
          </button>
        </div>
      </div>

      {err && <div className="analytics-empty" style={{ color: 'var(--color-error-dark)' }}>{err}</div>}

      {/* فیلترها */}
      <div className="analytics-section" style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          className="f-input"
          style={{ maxWidth: 260, marginBottom: 0 }}
          placeholder="جستجو در نام / کد / منبع…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="f-input"
          style={{ maxWidth: 140, marginBottom: 0 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
        >
          <option value="all">همهٔ وضعیت‌ها</option>
          <option value="active">فعال</option>
          <option value="inactive">غیرفعال</option>
        </select>
        <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
          {fa(filtered.length)} کمپین
        </span>
      </div>

      <div className="analytics-section">
        {filtered.length === 0 && !loading ? (
          <div className="analytics-empty">
            {campaigns.length === 0 ? 'هنوز کمپینی ساخته نشده. با «کمپین جدید» شروع کنید.' : 'کمپینی با این فیلتر پیدا نشد.'}
          </div>
        ) : (
          <>
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>نام</th>
                  <th style={{ width: 90 }}>کانال</th>
                  <th>کد / لینک کوتاه</th>
                  <th
                    style={{ width: 110 }}
                    title="کلیک انسانی در بازهٔ انتخابی / کل کلیک انسانی از ابتدا · کلیک بات‌ها (پیش‌نمایش پیام‌رسان‌ها) جدا شمرده می‌شود · «—» = کمپین قدیمی بدون شمارش روزانه"
                  >
                    کلیک (بازه/کل)
                  </th>
                  <th style={{ width: 70 }}>سشن</th>
                  <th style={{ width: 80 }}>بازدیدکننده</th>
                  <th style={{ width: 70 }}>تبدیل</th>
                  <th style={{ width: 80 }} title="سشن‌های بازه ÷ کلیک انسانی بازه">نرخ ورود</th>
                  <th style={{ width: 70 }}>وضعیت</th>
                  <th style={{ width: 200 }}>اقدامات</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((c) => (
                  <tr key={c._id}>
                    <td>
                      <strong>{c.name}</strong>
                      {c.description && <div style={{ fontSize: 11, color: 'var(--color-text-disabled)' }}>{c.description}</div>}
                    </td>
                    <td>{channelChip(c)}</td>
                    <td>
                      <div style={{ direction: 'ltr', textAlign: 'right' }}>
                        <code style={{ fontSize: 12 }}>{c.shortLink}</code>
                        <button className="copy-btn" onClick={() => copy(c.shortLink)} style={{ marginInlineStart: 6 }}>
                          {copied === c.shortLink ? '✓' : 'کپی'}
                        </button>
                      </div>
                    </td>
                    <td>
                      {c.clicksInRange === null || c.clicksInRange === undefined ? '—' : fa(c.clicksInRange)}
                      {' / '}
                      {fa(c.clicks)}
                    </td>
                    <td>{fa(c.stats?.sessions)}</td>
                    <td>{fa(c.stats?.visitors)}</td>
                    <td>{fa(c.stats?.conversions)}</td>
                    <td>{entryRate(c.stats?.sessions, c.clicksInRange)}</td>
                    <td>
                      {c.isActive ? (
                        <span className="change up">فعال</span>
                      ) : (
                        <span className="change flat">غیرفعال</span>
                      )}
                    </td>
                    <td>
                      <button className="link-btn" onClick={() => loadInsights(c)}>آمار</button>
                      {' · '}
                      <button className="link-btn" onClick={() => openEdit(c)}>ویرایش</button>
                      {' · '}
                      <button className="link-btn" onClick={() => toggleActive(c)}>
                        {c.isActive ? 'غیرفعال‌سازی' : 'فعال‌سازی'}
                      </button>
                      {' · '}
                      <button className="link-btn danger" onClick={() => remove(c)}>حذف</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12, alignItems: 'center' }}>
                <button className="link-btn" disabled={pageSafe <= 1} onClick={() => setPage(pageSafe - 1)}>
                  → قبلی
                </button>
                <span style={{ fontSize: 12 }}>
                  صفحهٔ {fa(pageSafe)} از {fa(totalPages)}
                </span>
                <button className="link-btn" disabled={pageSafe >= totalPages} onClick={() => setPage(pageSafe + 1)}>
                  بعدی ←
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ===== Create/Edit dialog ===== */}
      {dialogOpen && (
        <div className="analytics-modal-backdrop" onClick={() => !saving && setDialogOpen(false)}>
          <div className="analytics-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editing ? 'ویرایش کمپین' : 'کمپین جدید'}</h3>
            {formErr && <div className="analytics-empty" style={{ color: 'var(--color-error-dark)', textAlign: 'right' }}>{formErr}</div>}

            {!editing && (
              <>
                <label className="f-label">کانال (پیشنهادی — utm را خودکار پر می‌کند)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                  {CHANNEL_PRESETS.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => applyPreset(p)}
                      style={{
                        fontSize: 12,
                        padding: '4px 10px',
                        borderRadius: 12,
                        border: '1px solid var(--color-border, rgba(127,127,127,.3))',
                        background:
                          form.utmSource === p.utmSource && form.utmMedium === p.utmMedium
                            ? PALETTE.primary
                            : 'transparent',
                        color:
                          form.utmSource === p.utmSource && form.utmMedium === p.utmMedium
                            ? '#fff'
                            : 'inherit',
                        cursor: 'pointer',
                      }}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </>
            )}

            <label className="f-label">نام کمپین *</label>
            <input className="f-input" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="مثلاً: کمپین عید ۱۴۰۴ اینستاگرام" />

            <label className="f-label">کد (utm_campaign / لینک کوتاه)</label>
            <input className="f-input" style={{ direction: 'ltr' }} value={form.code || ''} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="eid-1404 (اگر خالی بماند از نام ساخته می‌شود)" disabled={!!editing} />
            {editing && <div style={{ fontSize: 11, color: 'var(--color-text-disabled)', marginBottom: 6 }}>تغییر کد، تاریخچه‌ی آمار قبلی را جدا می‌کند؛ این فیلد قفل است.</div>}

            <label className="f-label">مقصد (مسیر یا URL)</label>
            <input className="f-input" style={{ direction: 'ltr' }} value={form.destinationPath || ''} onChange={(e) => setForm({ ...form, destinationPath: e.target.value })} placeholder="/products یا /product/123" />

            <div className="analytics-grid-2col" style={{ gap: 8 }}>
              <div>
                <label className="f-label">utm_source</label>
                <input className="f-input" style={{ direction: 'ltr' }} value={form.utmSource || ''} onChange={(e) => setForm({ ...form, utmSource: e.target.value })} placeholder="instagram" />
              </div>
              <div>
                <label className="f-label">utm_medium</label>
                <select className="f-input" value={form.utmMedium || ''} onChange={(e) => setForm({ ...form, utmMedium: e.target.value })}>
                  <option value="">—</option>
                  {MEDIUM_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>

            <div className="analytics-grid-2col" style={{ gap: 8 }}>
              <div>
                <label className="f-label">utm_term (اختیاری)</label>
                <input className="f-input" style={{ direction: 'ltr' }} value={form.utmTerm || ''} onChange={(e) => setForm({ ...form, utmTerm: e.target.value })} />
              </div>
              <div>
                <label className="f-label">utm_content (اختیاری)</label>
                <input className="f-input" style={{ direction: 'ltr' }} value={form.utmContent || ''} onChange={(e) => setForm({ ...form, utmContent: e.target.value })} placeholder="story / post / bio" />
              </div>
            </div>

            <label className="f-label">توضیحات (اختیاری)</label>
            <input className="f-input" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />

            <label className="f-checkbox">
              <input type="checkbox" checked={!!form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
              کمپین فعال است (لینک کوتاه کار می‌کند)
            </label>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start', marginTop: 14 }}>
              <button onClick={save} disabled={saving} style={{ background: PALETTE.primary, color: '#fff' }}>
                {saving ? 'در حال ذخیره…' : editing ? 'ذخیره تغییرات' : 'ساخت کمپین'}
              </button>
              <button onClick={() => setDialogOpen(false)} disabled={saving}>انصراف</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteAnalyticsCampaigns;
